/**
 * Fixed-timestep RENDER loop with interpolated drawing.
 *
 * The physics runs at exactly 120 Hz and nothing is allowed to vary that: the
 * Rocket League constants are tuned for that dt and the model is not
 * dt-invariant. Rendering runs at whatever the display gives us and
 * interpolates between the two most recent physics states.
 *
 * The alternative — stepping physics by the frame time — is what makes most
 * browser reimplementations feel wrong on a 144 Hz monitor and wrong again on
 * a 60 Hz one. It is not an option here.
 *
 * WHY THIS LIVES UNDER src/render. It is the only part of the loop that needs
 * `performance.now` and `requestAnimationFrame`, i.e. the only part that is
 * host-dependent. The core simulation must be steppable with no clock at all —
 * a training run wants ticks as fast as the CPU will produce them, and a worker
 * thread has no rAF. The headless driver the core does need is `runHeadless` in
 * src/sim/loop.ts, and `test/sim/decoupling.test.ts` enforces the split.
 */

import { DT } from '../physics/constants';

/**
 * Upper bound on how much simulated time a single frame may consume. Without
 * it, a long stall (tab backgrounded, GC pause, breakpoint) produces a huge
 * accumulated delta and the loop tries to catch up with hundreds of ticks,
 * which stalls it further. Beyond this we drop the excess and let the
 * simulation fall behind wall-clock instead — the "spiral of death" guard.
 */
const MAX_FRAME_TIME = 0.25;

export interface LoopCallbacks {
  /** Advance the simulation by exactly one fixed tick. */
  tick(dt: number, tickIndex: number): void;
  /**
   * Draw. `alpha` in [0, 1) is the fraction of a tick elapsed since the last
   * completed tick; use it to interpolate between the previous and current
   * physics states so motion is smooth at any refresh rate.
   */
  render(alpha: number, frameTime: number): void;
}

export class FixedLoop {
  private accumulator = 0;
  private lastTime = 0;
  private rafHandle: number | null = null;
  private running = false;

  /** Number of fixed ticks simulated since start. */
  tickIndex = 0;

  /** Ticks executed in the most recent frame — useful for a debug overlay. */
  lastFrameTicks = 0;

  constructor(private readonly callbacks: LoopCallbacks) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now() / 1000;
    this.accumulator = 0;
    this.schedule();
  }

  stop(): void {
    this.running = false;
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  private schedule(): void {
    this.rafHandle = requestAnimationFrame(() => this.frame());
  }

  private frame(): void {
    if (!this.running) return;

    const now = performance.now() / 1000;
    let frameTime = now - this.lastTime;
    this.lastTime = now;

    if (frameTime > MAX_FRAME_TIME) frameTime = MAX_FRAME_TIME;
    this.accumulator += frameTime;

    let ticks = 0;
    while (this.accumulator >= DT) {
      this.callbacks.tick(DT, this.tickIndex);
      this.tickIndex++;
      this.accumulator -= DT;
      ticks++;
    }
    this.lastFrameTicks = ticks;

    this.callbacks.render(this.accumulator / DT, frameTime);
    this.schedule();
  }
}
